/**
 * Subscription Monetization Interface — Plan Comparison
 *
 * PXC-B.2 refactor (FE-only, minimal):
 *   The legacy renderer was a wide checkbox matrix (technical feature flags
 *   in rows, packages in columns). The new renderer is a card grid that
 *   communicates the canonical Provider commercial model: capacity +
 *   commercial terms + support, with low-signal feature toggles folded into
 *   an optional "See details" expander.
 *
 * Strict constraints:
 *   • FE-only. NO backend / DTO / schema / engine / Stripe change.
 *   • Commission percentages come ONLY from /api/commission/preview.
 *   • NO hardcoded percentages.
 *   • NO bps / priority / rule_id / resolution_chain in DOM.
 *   • Backend is SSOT for package visibility — packages are rendered as-is.
 *   • Rollback path preserved via PXC_B2_COMPARE_PLANS_REFACTOR=false.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { getLocalizedField } from '../../context/LanguageContext';
import { Button } from '../ui/button';
import {
  Check, X, Crown, Zap, Building2, Clock, Phone,
  ArrowRight, ArrowDown, Infinity as InfinityIcon, Users,
  Sparkles, Megaphone, ChevronDown, Info,
} from 'lucide-react';
import api from '../../utils/api';

// ── PXC-B.2 feature flag ─────────────────────────────────────────────
//   true  → new card grid + commercial terms (default)
//   false → legacy wide table (rollback path, preserved untouched)
const PXC_B2_COMPARE_PLANS_REFACTOR = true;

// ── Legacy feature taxonomy (rollback only) ──────────────────────────
// PACKAGE-ENTITLEMENT-SSOT-REMEDIATION (2026-06): retired/unsupported
// features (api_access, bulk_operations, activity_timeline,
// advanced_analytics, custom_branding) removed from the provider surface.
const FEATURE_CATEGORIES = {
  limits: {
    label: 'Usage Limits',
    features: [
      { key: 'leads_per_month', label: 'Leads per month', type: 'limit' },
      { key: 'max_users', label: 'Included users', type: 'limit' },
      { key: 'offers_per_month', label: 'Offers per month', type: 'limit' },
    ],
  },
  core: {
    label: 'Core Features',
    features: [
      { key: 'leads_enabled', label: 'Leads Management', type: 'boolean' },
      { key: 'campaigns_enabled', label: 'Campaigns', type: 'boolean' },
    ],
  },
  advanced: {
    label: 'Service',
    features: [
      { key: 'priority_support', label: 'Priority Support', type: 'boolean' },
      { key: 'dedicated_support', label: 'Dedicated Support', type: 'boolean' },
    ],
  },
};

// ── Detail-toggles surfaced under the optional <See details> expander ─
const SECONDARY_FEATURE_KEYS = [
  { key: 'leads_enabled', label: 'Leads management' },
  { key: 'campaigns_enabled', label: 'Campaigns' },
  { key: 'priority_support', label: 'Priority support' },
  { key: 'dedicated_support', label: 'Dedicated support' },
];

// ── Calm tier icon mapping (no "popular" highlight, no urgency) ──────
const TIER_ICONS = {
  TRIAL: Clock,
  STARTER: Zap,
  PRO: Crown,
  ENTERPRISE: Building2,
};

const _fmtCapacity = (value) => {
  if (value === -1 || value === null || value === undefined) return 'Unlimited';
  return String(value);
};

const _fmtMoney = (amount, ccy) => {
  if (typeof amount !== 'number') return '—';
  const c = (ccy || '').toUpperCase();
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: c }).format(amount);
  } catch {
    return `${c} ${amount.toFixed(2)}`;
  }
};

const _commissionPctFromBps = (bps) => {
  if (typeof bps !== 'number') return null;
  return bps % 100 === 0 ? `${(bps / 100).toFixed(0)}` : `${(bps / 100).toFixed(1)}`;
};

// ── Hook: resolve marketplace + provider_owned commission per package ─
// All percentages come from /api/commission/preview only. We NEVER infer.
const usePackageCommissions = (packageIds) => {
  const [byPkg, setByPkg] = useState({});
  useEffect(() => {
    if (!packageIds || packageIds.length === 0) return;
    let cancelled = false;
    const channels = ['marketplace', 'provider_owned'];
    const fetchOne = async (pkgId, channel) => {
      try {
        const { data } = await api.get('/commission/preview', {
          params: { channel, package_id: pkgId, amount: 0 },
        });
        if (!data?.success || typeof data.commission_value !== 'number') return null;
        return _commissionPctFromBps(data.commission_value);
      } catch (e) {
        return null;
      }
    };
    (async () => {
      const next = {};
      await Promise.all(
        packageIds.map(async (pkgId) => {
          const [mp, po] = await Promise.all(channels.map((ch) => fetchOne(pkgId, ch)));
          next[pkgId] = { marketplace: mp, provider_owned: po };
        })
      );
      if (!cancelled) setByPkg(next);
    })();
    return () => { cancelled = true; };
  }, [packageIds.join('|')]);
  return byPkg;
};

// ── Single plan card ────────────────────────────────────────────────
const PlanCard = ({ pkg, isCurrent, currentRank, commission, onSelectPlan, onContactSales, t }) => {
  const [showDetails, setShowDetails] = useState(false);
  const TierIcon = TIER_ICONS[pkg.id] || Sparkles;

  const pkgRank = pkg.plan_tier_rank ?? pkg.upgrade_order ?? 0;
  const isUpgrade = currentRank !== null && pkgRank > currentRank;
  const isDowngrade = currentRank !== null && pkgRank < currentRank;

  const limits = pkg.limits || {};
  const features = pkg.features || {};
  const planName = getLocalizedField(pkg.name, 'en') || pkg.id;

  // CTA logic — canonical: a published resolved price (purchasable) always wins
  // over the legacy contact_sales flag, so every surface shows the same truth.
  let ctaNode = null;
  if (!isCurrent && pkg.purchasable === false && !pkg.requires_contact_sales) {
    // CANONICAL SSOT fail-closed: no published price for this jurisdiction.
    ctaNode = (
      <Button disabled variant="outline" className="w-full min-h-[40px] text-amber-600" data-testid={`price-unavailable-cta-${pkg.id}`}>
        {t('packageSelection.priceUnavailableCta', 'Pricing unavailable')}
      </Button>
    );
  } else if (pkg.requires_contact_sales && !pkg.purchasable) {
    ctaNode = (
      <Button
        variant="outline"
        className="w-full border-slate-300 text-slate-700 hover:bg-slate-50 min-h-[40px]"
        onClick={() => onContactSales?.()}
        data-testid={`contact-sales-${pkg.id}`}
      >
        <Phone className="h-4 w-4 mr-2" />
        {t('subscription.contactSales', 'Contact sales')}
      </Button>
    );
  } else if (isCurrent) {
    ctaNode = (
      <Button disabled variant="outline" className="w-full min-h-[40px]" data-testid={`current-plan-${pkg.id}`}>
        {t('subscription.currentPlan', 'Current package')}
      </Button>
    );
  } else if (isUpgrade) {
    ctaNode = (
      <Button
        className="w-full min-h-[40px]"
        onClick={() => onSelectPlan?.(pkg.id, 'upgrade', pkg)}
        data-testid={`upgrade-to-${pkg.id}`}
      >
        {t('subscription.choosePlan', 'Choose package')}
        <ArrowRight className="h-4 w-4 ml-2" />
      </Button>
    );
  } else if (isDowngrade) {
    ctaNode = (
      <Button
        variant="outline"
        className="w-full text-slate-700 min-h-[40px]"
        onClick={() => onSelectPlan?.(pkg.id, 'downgrade')}
        data-testid={`downgrade-to-${pkg.id}`}
      >
        <ArrowDown className="h-4 w-4 mr-2" />
        {t('subscription.switchPlan', 'Switch package')}
      </Button>
    );
  } else {
    ctaNode = (
      <Button
        className="w-full min-h-[40px]"
        onClick={() => onSelectPlan?.(pkg.id, 'select')}
        data-testid={`select-${pkg.id}`}
      >
        {t('subscription.choosePlan', 'Choose package')}
      </Button>
    );
  }

  // Commission row component (mirrors PXC-B.1 hero copy / no bps in DOM)
  const CommissionRow = ({ label, helper, value, testidPrefix }) => (
    <div
      className="flex items-start justify-between gap-3 py-2 px-3 rounded-md border border-slate-200 bg-slate-50/60"
      data-testid={`${testidPrefix}-row`}
    >
      <div className="min-w-0">
        <div className="text-[11px] font-medium text-slate-700">{label}</div>
        <div className="text-[10px] text-slate-500 mt-0.5">{helper}</div>
      </div>
      <div className="shrink-0 text-right">
        {value ? (
          <span className="text-sm font-semibold text-slate-900 tabular-nums" data-testid={`${testidPrefix}-value`}>
            {value}%
          </span>
        ) : (
          <span className="text-[11px] text-slate-400" data-testid={`${testidPrefix}-empty`}>
            Not configured
          </span>
        )}
      </div>
    </div>
  );

  return (
    <Card
      className={`border ${isCurrent ? 'border-blue-300 ring-1 ring-blue-200' : 'border-slate-200'} transition-all duration-200 hover:border-slate-300 flex flex-col`}
      data-testid={`plan-card-${pkg.id}`}
    >
      <CardContent className="p-4 flex flex-col gap-3 grow">
        {/* SECTION 1 — Plan header */}
        <div className="flex items-start justify-between gap-2" data-testid={`plan-card-${pkg.id}-header`}>
          <div className="flex items-center gap-2 min-w-0">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-slate-100 text-slate-700">
              <TierIcon className="w-4 h-4" />
            </span>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-900 truncate" data-testid={`plan-card-${pkg.id}-name`}>
                {planName}
              </div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500">
                {t('subscription.providerPlan', 'Provider package')}
              </div>
            </div>
          </div>
          {isCurrent && (
            <Badge
              variant="outline"
              className="border-blue-200 bg-blue-50 text-blue-700 text-[10px] shrink-0"
              data-testid={`plan-card-${pkg.id}-current-badge`}
            >
              {t('subscription.yourPlan', 'Current')}
            </Badge>
          )}
        </div>

        {/* Price — published resolved price wins over legacy contact_sales */}
        <div className="flex items-baseline gap-1" data-testid={`plan-card-${pkg.id}-price`}>
          {pkg.amount != null && pkg.amount > 0 ? (
            <>
              <span className="text-3xl font-bold text-slate-900">{_fmtMoney(pkg.amount, pkg.currency)}</span>
              <span className="text-slate-500 text-sm">/{pkg.billing_interval === 'yearly' ? t('subscription.yr', 'yr') : t('subscription.mo', 'mo')}</span>
            </>
          ) : pkg.requires_contact_sales ? (
            <span className="text-2xl font-bold text-slate-900">Custom</span>
          ) : (pkg.amount == null) ? (
            <span className="text-sm font-medium text-amber-600">{t('packageSelection.priceUnavailableCta', 'Pricing unavailable')}</span>
          ) : (
            <span className="text-2xl font-bold text-slate-900">{t('subscription.free', 'Free')}</span>
          )}
        </div>

        {/* SECTION 2 — Capacity */}
        <div className="space-y-1 text-xs" data-testid={`plan-card-${pkg.id}-capacity`}>
          <div className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
            {t('subscription.capacity', 'Capacity')}
          </div>
          <div className="flex items-center justify-between" data-testid={`plan-card-${pkg.id}-cap-users`}>
            <span className="text-slate-600 inline-flex items-center gap-1.5">
              <Users className="w-3 h-3 text-slate-400" /> {t('subscription.includedUsers', 'Included users')}
            </span>
            <span className="font-medium text-slate-900 tabular-nums">
              {limits.sales_reps === -1 ? <InfinityIcon className="inline w-3.5 h-3.5" /> : _fmtCapacity(limits.sales_reps)}
            </span>
          </div>
          <div className="flex items-center justify-between" data-testid={`plan-card-${pkg.id}-cap-leads`}>
            <span className="text-slate-600 inline-flex items-center gap-1.5">
              <Sparkles className="w-3 h-3 text-slate-400" /> {t('subscription.marketplaceLeads', 'Marketplace leads / month')}
            </span>
            <span className="font-medium text-slate-900 tabular-nums">
              {limits.leads_per_month === -1 ? <InfinityIcon className="inline w-3.5 h-3.5" /> : _fmtCapacity(limits.leads_per_month)}
            </span>
          </div>
          <div className="flex items-center justify-between" data-testid={`plan-card-${pkg.id}-cap-campaigns`}>
            <span className="text-slate-600 inline-flex items-center gap-1.5">
              <Megaphone className="w-3 h-3 text-slate-400" /> {t('subscription.activeCampaigns', 'Active campaigns')}
            </span>
            <span className="font-medium text-slate-900 tabular-nums">
              {limits.campaigns_active_max === -1 ? <InfinityIcon className="inline w-3.5 h-3.5" /> : _fmtCapacity(limits.campaigns_active_max)}
            </span>
          </div>
          {typeof limits.before_after_quota === 'number' && (
            <div className="flex items-center justify-between" data-testid={`plan-card-${pkg.id}-cap-ba`}>
              <span className="text-slate-600">{t('subscription.beforeAfterQuota', 'Before/after quota')}</span>
              <span className="font-medium text-slate-900 tabular-nums">
                {limits.before_after_quota === -1 ? <InfinityIcon className="inline w-3.5 h-3.5" /> : _fmtCapacity(limits.before_after_quota)}
              </span>
            </div>
          )}
        </div>

        {/* SECTION 3 — Commercial terms */}
        <div className="space-y-1.5" data-testid={`plan-card-${pkg.id}-commercial-terms`}>
          <div className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
            {t('subscription.commercialTerms', 'Commercial terms')}
          </div>
          <CommissionRow
            label={t('subscription.marketplaceCommissionLabel', 'Marketplace patient commission')}
            helper={t('subscription.marketplaceCommissionHelper', 'When TreatX brings the patient')}
            value={commission?.marketplace}
            testidPrefix={`plan-card-${pkg.id}-commission-marketplace`}
          />
          <CommissionRow
            label={t('subscription.providerOwnedCommissionLabel', 'Provider-owned patient commission')}
            helper={t('subscription.providerOwnedCommissionHelper', 'When you bring the patient')}
            value={commission?.provider_owned}
            testidPrefix={`plan-card-${pkg.id}-commission-provider-owned`}
          />
        </div>

        {/* SECTION 4 — Support */}
        <div
          className="flex items-center gap-1.5 text-[11px] text-slate-600"
          data-testid={`plan-card-${pkg.id}-support`}
        >
          <Check className="w-3.5 h-3.5 text-emerald-600" />
          <span>{t('subscription.fullTreatXSupport', 'Full TreatX support')}</span>
        </div>

        {/* See details (clutter dampener) */}
        <details
          className="border-t border-slate-100 pt-2"
          data-testid={`plan-card-${pkg.id}-details`}
          onToggle={(e) => setShowDetails(e.currentTarget.open)}
        >
          <summary
            className="text-[11px] text-slate-500 cursor-pointer inline-flex items-center gap-1 hover:text-slate-700 select-none list-none"
            data-testid={`plan-card-${pkg.id}-details-summary`}
          >
            <ChevronDown className={`w-3 h-3 transition-transform ${showDetails ? 'rotate-180' : ''}`} />
            {t('subscription.seeDetails', 'See details')}
          </summary>
          <ul className="mt-2 space-y-0.5 text-[11px]">
            {SECONDARY_FEATURE_KEYS.map((f) => {
              const on = !!features[f.key];
              return (
                <li
                  key={f.key}
                  className="flex items-center justify-between"
                  data-testid={`plan-card-${pkg.id}-detail-${f.key}`}
                >
                  <span className="text-slate-600">{f.label}</span>
                  {on ? (
                    <Check className="w-3.5 h-3.5 text-emerald-600" />
                  ) : (
                    <X className="w-3.5 h-3.5 text-slate-300" />
                  )}
                </li>
              );
            })}
          </ul>
        </details>

        {/* Spacer pushes CTA to bottom */}
        <div className="grow" />

        {/* SECTION 5 — CTA */}
        <div data-testid={`plan-card-${pkg.id}-cta`}>{ctaNode}</div>
      </CardContent>
    </Card>
  );
};

// ── New card grid renderer ──────────────────────────────────────────
const PlanCardsCompare = ({
  packages,
  currentPackageKey,
  currentTierRank,
  onSelectPlan,
  onContactSales,
}) => {
  const { t } = useTranslation();
  // Stable ID list for the hook (memoized).
  const packageIds = useMemo(() => packages.map((p) => p.id), [packages]);
  const commissions = usePackageCommissions(packageIds);

  // Resolve effective rank — prefer prop, else look up from list.
  const effectiveRank =
    currentTierRank !== null && currentTierRank !== undefined
      ? currentTierRank
      : packages.find((p) => p.id === currentPackageKey)?.plan_tier_rank ?? null;

  // Apply the same TRIAL filter as the legacy table: hide TRIAL unless
  // the caller is on TRIAL (it's a starter onramp, not a tier to choose).
  const displayPackages = packages.filter((p) => p.id !== 'TRIAL' || currentPackageKey === 'TRIAL');

  // Grid columns scale with package count but never exceed 4 on lg+.
  const cols = Math.min(displayPackages.length, 4);
  const gridClass =
    cols >= 4 ? 'lg:grid-cols-4'
    : cols === 3 ? 'lg:grid-cols-3'
    : cols === 2 ? 'lg:grid-cols-2'
    : 'lg:grid-cols-1';

  return (
    <div data-testid="plan-comparison-cards">
      <div className={`grid grid-cols-1 md:grid-cols-2 ${gridClass} gap-4`}>
        {displayPackages.map((pkg) => (
          <PlanCard
            key={pkg.id}
            pkg={pkg}
            isCurrent={pkg.id === currentPackageKey}
            currentRank={effectiveRank}
            commission={commissions[pkg.id]}
            onSelectPlan={onSelectPlan}
            onContactSales={onContactSales}
            t={t}
          />
        ))}
      </div>
      <div className="mt-3 inline-flex items-start gap-1.5 text-[11px] text-slate-500" data-testid="plan-comparison-footnote">
        <Info className="w-3 h-3 mt-px shrink-0" />
        <span>
          {t(
            'subscription.commissionFootnote',
            'Commission rates are live from your active plan. Existing bookings keep the rate they were sold at.'
          )}
        </span>
      </div>
    </div>
  );
};

// ── Legacy table renderer (rollback path, untouched semantics) ───────
const LegacyPlanComparisonTable = ({
  currentPackageKey, currentTierRank, onSelectPlan, onContactSales, showCurrentBadge, packages,
}) => {
  const { t } = useTranslation();

  const packageStyles = {
    TRIAL: { icon: Clock, gradient: 'from-blue-500 to-blue-600', border: 'border-blue-200', bg: 'bg-blue-50', badge: 'bg-blue-100 text-blue-700' },
    STARTER: { icon: Zap, gradient: 'from-green-500 to-green-600', border: 'border-green-200', bg: 'bg-green-50', badge: 'bg-green-100 text-green-700' },
    PRO: { icon: Crown, gradient: 'from-purple-500 to-purple-600', border: 'border-purple-200', bg: 'bg-purple-50', badge: 'bg-purple-100 text-purple-700', popular: true },
    ENTERPRISE: { icon: Building2, gradient: 'from-amber-500 to-amber-600', border: 'border-amber-200', bg: 'bg-amber-50', badge: 'bg-amber-100 text-amber-700' },
  };

  const formatLimit = (value) => (value === -1 ? <InfinityIcon className="h-4 w-4 text-green-600" /> : value);
  const getFeatureValue = (pkg, featureKey, type) => {
    if (pkg.limits && featureKey in pkg.limits) {
      return type === 'limit' ? formatLimit(pkg.limits[featureKey]) : pkg.limits[featureKey];
    }
    if (pkg.features && featureKey in pkg.features) return pkg.features[featureKey];
    return false;
  };
  const renderFeatureCell = (pkg, feature) => {
    const value = getFeatureValue(pkg, feature.key, feature.type);
    if (feature.type === 'limit') return <span className="font-medium text-gray-900">{value}</span>;
    if (feature.type === 'boolean') return value ? <Check className="h-5 w-5 text-green-600 mx-auto" /> : <X className="h-5 w-5 text-gray-300 mx-auto" />;
    return value;
  };
  const getPackageCTA = (pkg, isCurrentPlan, currentRank) => {
    if (!pkg.requires_contact_sales && !isCurrentPlan && pkg.purchasable === false) {
      return (<Button disabled variant="outline" className="w-full text-amber-600" data-testid={`price-unavailable-cta-${pkg.id}`}>{t('packageSelection.priceUnavailableCta', 'Pricing unavailable')}</Button>);
    }
    const pkgRank = pkg.plan_tier_rank ?? pkg.upgrade_order ?? 0;
    if (pkg.requires_contact_sales && !pkg.purchasable) return (<Button variant="outline" className="w-full border-amber-300 text-amber-700 hover:bg-amber-50" onClick={() => onContactSales?.()} data-testid={`contact-sales-${pkg.id}`}><Phone className="h-4 w-4 mr-2" />{t('subscription.contactSales', 'Contact Sales')}</Button>);
    if (isCurrentPlan) return (<Button disabled className="w-full" variant="outline" data-testid={`current-plan-${pkg.id}`}>{t('subscription.currentPlan', 'Current Plan')}</Button>);
    if (pkgRank > currentRank) return (<Button className={`w-full bg-gradient-to-r ${packageStyles[pkg.id]?.gradient || 'from-blue-500 to-blue-600'}`} onClick={() => onSelectPlan?.(pkg.id, 'upgrade', pkg)} data-testid={`upgrade-to-${pkg.id}`}>{t('subscription.upgrade', 'Upgrade')}<ArrowRight className="h-4 w-4 ml-2" /></Button>);
    if (pkgRank < currentRank) return (<Button variant="outline" className="w-full text-gray-600" onClick={() => onSelectPlan?.(pkg.id, 'downgrade')} data-testid={`downgrade-to-${pkg.id}`}><ArrowDown className="h-4 w-4 mr-2" />{t('subscription.downgrade', 'Downgrade')}</Button>);
    return (<Button className="w-full" onClick={() => onSelectPlan?.(pkg.id, 'select')} data-testid={`select-${pkg.id}`}>{t('subscription.selectPlan', 'Select Plan')}</Button>);
  };

  const currentPackageRank = currentTierRank ?? packages.find(p => p.id === currentPackageKey)?.plan_tier_rank ?? -1;
  const displayPackages = packages.filter(p => p.id !== 'TRIAL' || currentPackageKey === 'TRIAL');

  return (
    <div className="overflow-x-auto" data-testid="plan-comparison-table">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <th className="p-4 text-left bg-gray-50 rounded-tl-lg min-w-[200px]">
              <span className="text-sm font-semibold text-gray-600">{t('subscription.comparePlans', 'Compare Plans')}</span>
            </th>
            {displayPackages.map((pkg) => {
              const style = packageStyles[pkg.id] || packageStyles.STARTER;
              const Icon = style.icon;
              const isCurrentPlan = pkg.id === currentPackageKey;
              return (
                <th key={pkg.id} className={`p-4 min-w-[180px] ${isCurrentPlan ? style.bg : 'bg-white'} ${isCurrentPlan ? style.border : ''} border-t-4 ${isCurrentPlan ? style.border.replace('border-', 'border-t-') : 'border-t-transparent'} relative`}>
                  {style.popular && (<div className="absolute -top-3 left-1/2 -translate-x-1/2"><Badge className="bg-purple-600 text-white text-xs px-2 py-0.5"><Sparkles className="h-3 w-3 mr-1" />{t('subscription.popular', 'Popular')}</Badge></div>)}
                  {showCurrentBadge && isCurrentPlan && (<Badge className={`${style.badge} text-xs mb-2`}>{t('subscription.yourPlan', 'Your Plan')}</Badge>)}
                  <div className="flex flex-col items-center gap-2">
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-r ${style.gradient} text-white flex items-center justify-center`}><Icon className="h-5 w-5" /></div>
                    <h3 className="font-bold text-gray-900">{getLocalizedField(pkg.name, 'en')}</h3>
                  </div>
                  <div className="mt-3">
                    {pkg.amount != null && pkg.amount > 0 ? (<div><span className="text-2xl font-bold text-gray-900">{_fmtMoney(pkg.amount, pkg.currency)}</span><span className="text-gray-500 text-sm">/{pkg.billing_interval === 'yearly' ? 'yr' : 'mo'}</span></div>)
                      : pkg.requires_contact_sales ? <span className="text-lg font-bold text-gray-900">Custom</span>
                      : pkg.amount == null ? <span className="text-sm font-medium text-amber-600">{t('packageSelection.priceUnavailableCta', 'Pricing unavailable')}</span>
                      : <span className="text-lg font-bold text-gray-900">{t('subscription.free', 'Free')}</span>}
                  </div>
                  <div className="mt-4">{getPackageCTA(pkg, isCurrentPlan, currentPackageRank)}</div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {Object.entries(FEATURE_CATEGORIES).map(([categoryKey, category]) => (
            <React.Fragment key={categoryKey}>
              <tr><td colSpan={displayPackages.length + 1} className="bg-gray-100 px-4 py-2"><span className="text-sm font-semibold text-gray-700">{t(`subscription.category.${categoryKey}`, category.label)}</span></td></tr>
              {category.features.map((feature) => (
                <tr key={feature.key} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="p-4 text-sm text-gray-700">{t(`subscription.feature.${feature.key}`, feature.label)}</td>
                  {displayPackages.map((pkg) => {
                    const isCurrentPlan = pkg.id === currentPackageKey;
                    const style = packageStyles[pkg.id];
                    return (<td key={`${pkg.id}-${feature.key}`} className={`p-4 text-center ${isCurrentPlan ? style?.bg : ''}`}>{renderFeatureCell(pkg, feature)}</td>);
                  })}
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ── Public component (props preserved for backwards compat) ──────────
const PlanComparisonTable = ({
  currentPackageKey = 'TRIAL',
  currentTierRank = null,
  onSelectPlan,
  onContactSales,
  showCurrentBadge = true,
}) => {
  const { t } = useTranslation();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const [pkgRes, priceRes] = await Promise.allSettled([
          api.get('/subscription/packages'),
          api.get('/provider/pricing/packages'),
        ]);
        const rawPkgs = pkgRes.status === 'fulfilled' ? (pkgRes.value.data.packages || []) : [];
        // Canonical resolved price map (per provider jurisdiction, fail-closed).
        const priceMap = {};
        if (priceRes.status === 'fulfilled') {
          (priceRes.value.data.packages || []).forEach((p) => {
            if (p && p.package_key) priceMap[p.package_key] = p;  // key ONLY by package_key
          });
        }
        const merged = rawPkgs.map((pk) => {
          const key = pk.package_key || pk.id;  // public list sets id = package_key
          const r = (key && priceMap[key]) ? priceMap[key] : null;
          const purchasable = !!(r && r.purchasable && r.amount != null && r.currency);
          return {
            ...pk,
            amount: purchasable ? r.amount : null,
            currency: purchasable ? r.currency : null,
            purchasable,
          };
        });
        const sorted = merged.sort((a, b) => (a.upgrade_order || 0) - (b.upgrade_order || 0));
        setPackages(sorted);
      } catch (error) {
        console.error('[PlanComparison] Error fetching packages:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPackages();
  }, []);

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400 mx-auto" />
          <p className="mt-4 text-gray-500">{t('common.loading', 'Loading...')}</p>
        </CardContent>
      </Card>
    );
  }

  if (PXC_B2_COMPARE_PLANS_REFACTOR) {
    return (
      <PlanCardsCompare
        packages={packages}
        currentPackageKey={currentPackageKey}
        currentTierRank={currentTierRank}
        onSelectPlan={onSelectPlan}
        onContactSales={onContactSales}
      />
    );
  }

  return (
    <LegacyPlanComparisonTable
      packages={packages}
      currentPackageKey={currentPackageKey}
      currentTierRank={currentTierRank}
      onSelectPlan={onSelectPlan}
      onContactSales={onContactSales}
      showCurrentBadge={showCurrentBadge}
    />
  );
};

export default PlanComparisonTable;
